import { useState, useEffect, useRef } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { userScheduleApi } from '../../api'
import type { DayOfWeek, UserScheduleEntry } from '../../types/api'
import { Button, Card, DashedPlaceholder } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'

const DEFAULT_TIME = '07:00'

interface DayConfig {
  value: DayOfWeek
  abbr: string
  label: string
}

export default function TrainingSchedulePage() {
  const theme = useTheme()
  const { t } = useTranslation()

  const DAYS: DayConfig[] = [
    { value: 'MONDAY',    abbr: t.common.dayAbbr.mon, label: t.common.day.monday },
    { value: 'TUESDAY',   abbr: t.common.dayAbbr.tue, label: t.common.day.tuesday },
    { value: 'WEDNESDAY', abbr: t.common.dayAbbr.wed, label: t.common.day.wednesday },
    { value: 'THURSDAY',  abbr: t.common.dayAbbr.thu, label: t.common.day.thursday },
    { value: 'FRIDAY',    abbr: t.common.dayAbbr.fri, label: t.common.day.friday },
    { value: 'SATURDAY',  abbr: t.common.dayAbbr.sat, label: t.common.day.saturday },
    { value: 'SUNDAY',    abbr: t.common.dayAbbr.sun, label: t.common.day.sunday },
  ]

  const [schedule, setSchedule] = useState<Map<DayOfWeek, string>>(new Map())
  const [savedSchedule, setSavedSchedule] = useState<Map<DayOfWeek, string>>(new Map())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function mapsEqual(a: Map<DayOfWeek, string>, b: Map<DayOfWeek, string>): boolean {
    if (a.size !== b.size) return false
    for (const [key, val] of a) {
      if (b.get(key) !== val) return false
    }
    return true
  }

  const isDirty = !mapsEqual(schedule, savedSchedule)

  useEffect(() => {
    let cancelled = false
    userScheduleApi.getSchedule().then(entries => {
      if (cancelled) return
      const map = new Map<DayOfWeek, string>()
      entries.forEach(e => map.set(e.day, e.startTime.substring(0, 5)))
      setSchedule(map)
      setSavedSchedule(map)
    }).catch(() => {
      if (!cancelled) setError(t.preferences.trainingSchedule.error.loadFailed)
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [t])

  useEffect(() => () => {
    if (successTimer.current) clearTimeout(successTimer.current)
  }, [])

  function toggleDay(day: DayOfWeek) {
    setSchedule(prev => {
      const next = new Map(prev)
      if (next.has(day)) {
        next.delete(day)
      } else {
        next.set(day, DEFAULT_TIME)
      }
      return next
    })
    setSuccess(false)
  }

  function setTime(day: DayOfWeek, time: string) {
    setSchedule(prev => new Map(prev).set(day, time))
    setSuccess(false)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    setSuccess(false)

    const entries: UserScheduleEntry[] = DAYS
      .filter(d => schedule.has(d.value))
      .map(d => ({ day: d.value, startTime: schedule.get(d.value)! }))

    try {
      const saved = await userScheduleApi.saveSchedule(entries)
      const map = new Map<DayOfWeek, string>()
      saved.forEach(e => map.set(e.day, e.startTime.substring(0, 5)))
      setSchedule(map)
      setSavedSchedule(map)
      setSuccess(true)
      successTimer.current = setTimeout(() => setSuccess(false), 3000)
    } catch {
      setError(t.preferences.trainingSchedule.error.saveFailed)
    } finally {
      setSaving(false)
    }
  }

  const selectedDays = DAYS.filter(d => schedule.has(d.value))

  if (loading) {
    return <div className="p-5 text-center opacity-60 pt-16">{t.common.loading}</div>
  }

  return (
    <div className="p-5 pb-28">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.preferences.trainingSchedule.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.preferences.trainingSchedule.description}
        </p>
      </header>

      {/* Day picker */}
      <div className="mb-6">
        <div className="flex justify-between gap-1">
          {DAYS.map(({ value, abbr }) => {
            const active = schedule.has(value)
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleDay(value)}
                className="flex-1 flex flex-col items-center gap-1 py-1 select-none"
              >
                <span
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold transition-all active:scale-90"
                  style={active ? {
                    backgroundColor: theme.buttonColor,
                    color: theme.buttonTextColor,
                    boxShadow: `0 4px 12px ${theme.buttonColor}40`,
                  } : {
                    backgroundColor: theme.cardBg,
                    border: `1px solid ${theme.cardBorder}`,
                    color: theme.text.muted,
                  }}
                >
                  {abbr.charAt(0)}
                </span>
                <span
                  className="text-[10px] font-medium"
                  style={{ color: active ? theme.accent.text : theme.text.muted, opacity: active ? 1 : 0.5 }}
                >
                  {abbr}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Summary badge */}
      <div className="mb-6 flex items-center gap-2">
        <span
          className="px-3 py-1 rounded-full text-xs font-semibold"
          style={{
            backgroundColor: selectedDays.length > 0 ? `${theme.buttonColor}18` : theme.cardBg,
            border: `1px solid ${selectedDays.length > 0 ? `${theme.buttonColor}40` : theme.cardBorder}`,
            color: selectedDays.length > 0 ? theme.accent.text : theme.text.muted,
          }}
        >
          {selectedDays.length} {t.preferences.trainingSchedule.daysPerWeek}
        </span>
        {selectedDays.length > 0 && (
          <span className="text-xs" style={{ color: theme.text.muted }}>
            {t.preferences.trainingSchedule.tapToAdjustTime}
          </span>
        )}
      </div>

      {/* Time configuration */}
      {selectedDays.length > 0 ? (
        <Card className="p-0 overflow-hidden mb-4">
          {selectedDays.map((day, idx) => (
            <div
              key={day.value}
              className="flex items-center justify-between px-4 py-3.5"
              style={{
                borderBottom: idx < selectedDays.length - 1
                  ? `1px solid ${theme.cardBorder}`
                  : undefined,
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                  style={{
                    backgroundColor: `${theme.buttonColor}20`,
                    color: theme.accent.text,
                  }}
                >
                  {day.abbr.charAt(0)}
                </span>
                <span className="text-sm font-medium" style={{ color: theme.text.primary }}>
                  {day.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.text.muted, opacity: 0.6 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <input
                  type="time"
                  value={schedule.get(day.value)}
                  onChange={e => setTime(day.value, e.target.value)}
                  className="text-sm font-semibold bg-transparent border-none outline-none"
                  style={{ color: theme.text.primary, minWidth: 0 }}
                />
              </div>
            </div>
          ))}
        </Card>
      ) : (
        <DashedPlaceholder
          className="mb-4"
          icon="📅"
          title={t.preferences.trainingSchedule.emptyTitle}
          hint={t.preferences.trainingSchedule.emptyHint}
        />
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl p-4 mb-4 bg-red-500/10 border border-red-500/25">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="rounded-xl p-4 mb-4 bg-green-500/10 border border-green-500/25 flex items-center gap-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-green-500">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <p className="text-sm text-green-500 font-medium">{t.preferences.trainingSchedule.saveSuccess}</p>
        </div>
      )}

      {/* Save */}
      {(isDirty || saving) && (
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? t.preferences.trainingSchedule.saving : t.preferences.trainingSchedule.save}
        </Button>
      )}
    </div>
  )
}
